import React, { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import { ApiClient, PlayerId, SchoolId, TeamId } from '@suntex.fim/api-client';
import {
  AlgebraGoalsIndexChart,
  GoalsIndexChart,
} from '@suntex.fim/assessment';
import { AuthService } from '@suntex.fim/auth';
import {
  EntityLevel,
  GoalsIndexPhase,
  GradeLevel,
  Locale,
  SpecialGradeLevel,
} from '@suntex.fim/enums';
import {
  ModuleAccessGuard,
  PlayerDashboard,
  PlayerPathRedirect,
} from '@suntex.fim/player';
import { Rankings } from '@suntex.fim/rankings';
import { FocusEditor } from '@suntex.fim/team';

import { wrapForClassic } from './classic';
import { init } from './init';
import { wrapForPlayer } from './player';
import { wrapForRouting } from './routing';
import { wrapForTeam } from './team';

declare global {
  interface Window {
    env?: {
      PLAYER_BASE_ROUTE?: string;
      RANKINGS_BASE_ROUTE?: string;
    };
    fim2: typeof fim2;
  }
}

type PortableTaggedEnum<Map> = {
  [key in keyof Map]: Map[key];
};

const fim2 = {
  ensureInitialized: init,
  mountComponent,
  renderComponent,
  components: {
    AlgebraGoalsIndexChart: wrapForClassic(AlgebraGoalsIndexChart),
    GoalsIndexChart: wrapForClassic(GoalsIndexChart),
    ModuleAccessGuard,
    FocusEditor: wrapForClassic(wrapForTeam(FocusEditor)),
    PlayerDashboard: wrapForRouting(
      wrapForClassic(wrapForPlayer(PlayerDashboard)),
      window.env?.PLAYER_BASE_ROUTE ?? 'player',
      PlayerPathRedirect,
    ),
    Rankings: wrapForRouting(
      wrapForClassic(Rankings),
      window.env?.RANKINGS_BASE_ROUTE ?? 'rankings',
    ),
  },
  enums: {
    Locale,
    EntityLevel: EntityLevel as PortableTaggedEnum<typeof EntityLevel>,
    GoalsIndexPhase: GoalsIndexPhase as PortableTaggedEnum<
      typeof GoalsIndexPhase
    >,
    GradeLevel: GradeLevel as PortableTaggedEnum<typeof GradeLevel>,
    SpecialGradeLevel: SpecialGradeLevel as PortableTaggedEnum<
      typeof SpecialGradeLevel
    >,
  },
  services: {
    ApiClient,
    AuthService,
  },
  typesTemplates: {
    PlayerId: 0 as PlayerId,
    TeamId: 0 as TeamId,
    SchoolId: 0 as SchoolId,
  },
};

window.fim2 = fim2;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mountComponent<C extends React.FunctionComponent<any>>(
  rootElementId: string,
  component: C,
) {
  const rootElement = document.getElementById(rootElementId);

  if (rootElement == null) {
    throw new Error(`Root element with id '${rootElementId}' not found`);
  }

  const root = createRoot(rootElement);

  return {
    render: (props: Parameters<C>[0]) => {
      const element = createElement(component, props);
      root.render(element);
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderComponent<C extends React.FunctionComponent<any>>(
  rootElementId: string,
  component: C,
  props: Parameters<C>[0],
) {
  mountComponent(rootElementId, component).render(props);
}
